import React from 'react'
import PropTypes from 'prop-types'
import { Button } from '@material-ui/core'
import RemoveIcon from '@material-ui/icons/RemoveCircle'
import CopyToClipboard from 'react-copy-to-clipboard'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import { firestore } from './utilities/firebase'
import linkTypes from './enums/linkTypes'
import KitImage from './components/KitImage'
import Img from './components/Img'
import ItemLabel from './components/ItemLabel'
import errorTypes from './enums/errorTypes'
import messageTypes from './enums/messageTypes'
import connectPropTypes from './utilities/connectPropTypes'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import preventableBinder from './utilities/preventableBinder'
import settings from '../config/settings'
import styles from './KitEdit.icss'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'


let hasDraggedOnce = false
const itemsAccross = 3
const maxItems = 15

const SortableItem = SortableElement(({ value }) => (
	<div className={styles[`box${itemsAccross}`]}>
		{value && (
			<div className={styles.wrapper}>
				<span className={styles.container}>
					<Img
						className={styles.img}
						content={value.images}
						divisor={itemsAccross}
						fadein={!hasDraggedOnce}
						resolution="half"
					/>
					<Button
						className={styles.deleteButton}
						onClick={value.clickBinder(value.kitItem.item.itemKey, value.kitItem.colourName)}
					>
						<RemoveIcon className={styles.removeItemFromKit} />
					</Button>
					<ItemLabel>{value.kitItem.item.itemName}</ItemLabel>
				</span>
			</div>
		)}
	</div>
))

const SortableList = SortableContainer(({ items, addItemText, maxItemsReachedText, clickBinder, addItemClick }) => {
	const boxes = maxItems - (items ? items.length : 0) - 1
	const maxItemReached = items && items.length >= maxItems
	return (
		<div className={styles.kitItems}>
			{items && items.map((kitItem, idx) => {
				const images = kitItem.itemImages
				const objKey = `${idx}_${kitItem.item.itemKey}_${kitItem.colourName}`
				return <SortableItem key={objKey} index={idx} value={{ images, kitItem, clickBinder }} />
			})}
			{!maxItemReached && (
				<div key="addItemButton" className={styles[`box${itemsAccross}`]}>
					<div className={styles.wrapper} style={{ backgroundColor: '#999' }}>
						<span className={styles.container}>
							<button type="button" disabled={maxItemReached} className={styles.addItemButton} onClick={addItemClick}>{addItemText}</button>
						</span>
					</div>
				</div>
			)}
			{maxItemReached && (
				<div key="maxItemsReached" className={styles[`box${itemsAccross}`]}>
					<div className={styles.wrapper} style={{ backgroundColor: '#999' }}>
						<span className={styles.container}>
							<span className={styles.addItemButton}>{maxItemsReachedText}</span>
						</span>
					</div>
				</div>
			)}
			{boxes > 0 && Array.from({ length: boxes }).map((x, i) => (
				/* eslint-disable-next-line react/no-array-index-key */
				<div key={`xBox${i}`} className={styles[`box${itemsAccross}`]}>
					<div className={styles.wrapper} style={{ backgroundColor: '#eee' }} />
				</div>
			))}
		</div>
	)
})

class KitEdit extends React.Component {
	constructor(props) {
		super(props)
		const parsedSearch = getNormalisedParameters(this.props.location.search)

		this.state = {
			kitId: getDecodedURIComponent(parsedSearch.kitid || props.match.params.kitId),
			doneButtonFunc: cb => cb()
		}

		this.searchElement = null
		this.setSearchElementRef = (element) => {
			this.searchElement = element
		}

		this.bindHandleDeleteButtonClick = preventableBinder(this.bindHandleDeleteButtonClick.bind(this), 'itemForKit')
		this.handleAddItemClick = preventableBinder(this.handleAddItemClick.bind(this), 'itemForKit')
		this.handleTagsUpdate = this.handleTagsUpdate.bind(this)
		this.handleUploadImageError = this.handleUploadImageError.bind(this)
		this.kitUrl = this.kitUrl.bind(this)
		this.sortEndHandler = this.sortEndHandler.bind(this)
		this.setDoneButtonFunc = this.setDoneButtonFunc.bind(this)
	}

	componentDidMount() {
		this.props.appHeading({ text: this.context.translate('Create a kit') })
		this.props.appBottomBarValue(linkTypes.ADD)
		this.props.appShowBottomBar(false)
		this.props.appShowBottomNavBar(true)
		this.props.appBottomNavBarHandleClick(() => {
			const kitDraft = { ...this.props.kitDraft }
			if (kitDraft.items.length < 2) {
				this.props.appMessage(messageTypes.ERROR, this.context.translate('You must add at least 2 items to a Kit'), errorTypes.LOCALISEDERROR)
			} else {
				this.props.appNextButtonIsLoading(true)
				// save the kit
				this.props.kitDraftSet({ kitDraft }, {
					success: ({ kitId }) => {
						this.state.doneButtonFunc(kitId, (imageUrl) => {
							if (imageUrl) {
								//	save the kit a 2nd time, so we can set the kit's imageUrl and kitId
								this.props.kitDraftSetImageUrlAndKitId({ kitId, imageUrl }, {
									success: () => {
										const ref = firestore.collection('kits').doc(kitId)
										let unsubscribeObserver
										const timeoutRedirect = setTimeout(() => {
											unsubscribeObserver()
											this.props.appNextButtonIsLoading(false)
										}, 10000)
										unsubscribeObserver = ref.onSnapshot((docSnap) => {
											if (docSnap.exists && docSnap.data().images && docSnap.data().images.length) {
												clearTimeout(timeoutRedirect)
												unsubscribeObserver()
												this.props.kitDraftSetImages({ images: docSnap.data().images })
												this.props.appNextButtonIsLoading(false)
												this.props.appRedirect(`/kit/${kitId}`)
											}
										}, (err) => {
											console.error(`[ERROR]: ${err}`)
										})
									},
									fail: () => {
										this.props.appNextButtonIsLoading(false)
									}
								})
							}
						})
					},
					fail: () => {
						this.props.appNextButtonIsLoading(false)
					}
				})
			}
		})
		this.props.appBottomNavBarLabelNext(this.context.translate('NEXT'))
		if (this.state.kitId) {
			this.props.kitDraftFetch({ kitId: this.state.kitId })
		} else {
			this.props.kitDraftCreate()
		}
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.kitDraft && this.props.kitDraft) {
			if (this.props.kitDraft.uid !== this.props.user.uid && this.state.kitId) {
				this.props.appRedirect(`/kit/${this.state.kitId}`)
			}
		}
		if (this.props.kitDraft && !this.props.pageHasLoaded) {
			this.props.appPageLoaded(true)
		}
	}

	setDoneButtonFunc(doneButtonFunc) {
		this.setState({ doneButtonFunc })
	}

	bindHandleDeleteButtonClick(itemKey, colourName) {
		return () => {
			this.props.kitDraftItemDelete({ itemKey, colourName })
		}
	}

	handleAddItemClick() {
		this.props.searchParametersClear()
		this.props.appRedirect(`/kit/edit/search${this.state.kitId ? `/${this.state.kitId}` : ''}`)
	}

	handleTagsUpdate(tags) {
		this.props.kitDraftSetTags({ tags })
	}

	handleUploadImageError() {
		this.props.appMessage(messageTypes.ERROR, this.context.translate('Image upload failed'), errorTypes.UNKNOWNERROR)
	}

	sortEndHandler({ oldIndex, newIndex }) {
		this.props.kitDraftItemsSort({ oldIndex, newIndex })
	}

	kitUrl() {
		const name = this.props.kitDraft.name ? this.props.kitDraft.name.toLowerCase().replace(/[^a-z0-9]/gi, '-') : ''
		return `https://${settings.firebase.config.authDomain}/kit/${name}/${this.state.kitId}`
	}

	render() {
		const kitDraft = this.props.kitDraft
		return (
			<section>
				{kitDraft && (!this.state.kitId || (this.state.kitId && kitDraft.kitId === this.state.kitId)) && (
					<div className={styles.kitCreationSurround}>
						<section className={styles.leadImage}>
							<KitImage
								kitItems={kitDraft.items}
								kitTags={kitDraft.tags}
								kitTemplateSettings={kitDraft.templateSettings}
								handleTemplateUpdate={(templateSettings) => {
									this.props.kitDraftTemplateUpdate({ templateSettings })
								}}
								handleTagsUpdate={this.handleTagsUpdate}
								kitId={this.state.kitId}
								onCompleteBinder={this.setDoneButtonFunc}
							/>
						</section>

						<section className={styles.pagePadding} style={{ display: 'none' }}>
							<CopyToClipboard text={this.kitUrl()} onCopy={() => this.setState({ copied: true })}>
								<Button variant="contained">{this.state.copied ? this.context.translate('Copied!') : this.context.translate('Copy Kit URL to clipboard')}</Button>
							</CopyToClipboard>
						</section>

						<section className={styles.kitWrapper}>
							<SortableList
								axis="xy"
								pressDelay={100}
								useWindowAsScrollContainer
								items={kitDraft.items}
								addItemText={this.context.translate('ADD ITEM')}
								maxItemsReachedText={this.context.translate('Maximum items added ({maxItems})', { maxItems })}
								addItemClick={this.handleAddItemClick}
								clickBinder={this.bindHandleDeleteButtonClick}
								onSortStart={() => {
									if (global.navigator.vibrate) {
										global.navigator.vibrate(100)
									}
									hasDraggedOnce = true
								}}
								onSortEnd={this.sortEndHandler}
							/>
						</section>
					</div>
				)}
			</section>
		)
	}
}

KitEdit.contextTypes = {
	translate: PropTypes.func
}

KitEdit.propTypes = {
	location: PropTypes.shape({
		pathname: PropTypes.string.isRequired,
		search: PropTypes.string
	}).isRequired,
	match: PropTypes.shape({
		params: PropTypes.object.isRequired
	}).isRequired,
	appHeading: PropTypes.func.isRequired,
	appBottomBarValue: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	appMessage: PropTypes.func.isRequired,
	appShowBottomBar: PropTypes.func.isRequired,
	appShowBottomNavBar: PropTypes.func.isRequired,
	appBottomNavBarHandleClick: PropTypes.func.isRequired,
	appBottomNavBarLabelNext: PropTypes.func.isRequired,
	appNextButtonIsLoading: PropTypes.func.isRequired,
	appPageLoaded: PropTypes.func.isRequired,
	searchParametersClear: PropTypes.func.isRequired,
	pageHasLoaded: PropTypes.bool.isRequired,
	kitDraft: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitDraftFetch: PropTypes.func.isRequired,
	kitDraftItemDelete: PropTypes.func.isRequired,
	kitDraftSet: PropTypes.func.isRequired,
	kitDraftSetImages: PropTypes.func.isRequired,
	kitDraftSetImageUrlAndKitId: PropTypes.func.isRequired,
	kitDraftSetTags: PropTypes.func.isRequired,
	kitDraftTemplateUpdate: PropTypes.func.isRequired,
	kitDraftItemsSort: PropTypes.func.isRequired,
	kitDraftCreate: PropTypes.func.isRequired,
	user: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types
}

KitEdit.defaultProps = {
	kitDraft: undefined
}

export const testing = {
	KitEdit
}

export default connectPropTypes(KitEdit)



// WEBPACK FOOTER //
// ./src/KitEdit.jsx